// Equipment taxonomy used on Floor Plans and ACMS pages.
// Each entry gives an icon (lucide-react), colour accents and a status resolver
// that maps the site's live telemetry → a pin status ("ok" | "warn" | "fault" | "off").

import {
  Flame, Droplet, Thermometer, Zap, Radio, Siren, Volume2, Activity,
  Gauge, CircleDot, Wrench, Bell, Settings2, Cpu, Wifi,
} from "lucide-react";

export const EQUIPMENT = {
  smoke_detector: {
    label: "Smoke Detector", short: "SD", color: "#facc15",
    icon: CircleDot,
    group: "detection",
  },
  heat_detector: {
    label: "Heat Detector", short: "HD", color: "#fb923c",
    icon: Thermometer,
    group: "detection",
  },
  mcp: {
    label: "Manual Call Point", short: "MCP", color: "#ef4444",
    icon: Bell,
    group: "detection",
  },
  pa_speaker: {
    label: "PA Speaker", short: "PA", color: "#38bdf8",
    icon: Volume2,
    group: "pa",
  },
  fire_panel: {
    label: "Fire Panel", short: "FP", color: "#a855f7",
    icon: Cpu,
    group: "detection",
  },
  gateway: {
    label: "Gateway", short: "GW", color: "#6366f1",
    icon: Wifi,
    group: "infra",
  },
  pressure_sensor: {
    label: "Pressure Sensor", short: "PS", color: "#22d3ee",
    icon: Gauge,
    group: "sensor",
  },
  ultrasonic: {
    label: "Level Sensor", short: "LS", color: "#0ea5e9",
    icon: Droplet,
    group: "sensor",
  },
  dp_switch: {
    label: "DP Switch", short: "DP", color: "#14b8a6",
    icon: Activity,
    group: "sensor",
  },
  voltage_sensor: {
    label: "Voltage Sensor", short: "V", color: "#eab308",
    icon: Zap,
    group: "sensor",
  },
  valve: {
    label: "Valve", short: "VL", color: "#94a3b8",
    icon: Settings2,
    group: "infra",
  },
  // Pumps — each type is explicit so it can be marked separately on the plan
  hydrant_main_pump: {
    label: "Main Hydrant Pump", short: "HM", color: "#ff6b35",
    icon: Flame,
    group: "pump_hydrant",
  },
  hydrant_jockey_pump: {
    label: "Hydrant Jockey Pump", short: "HJ", color: "#f59e0b",
    icon: Wrench,
    group: "pump_hydrant",
  },
  sprinkler_main_pump: {
    label: "Main Sprinkler Pump", short: "SM", color: "#4fd1ff",
    icon: Droplet,
    group: "pump_sprinkler",
  },
  sprinkler_jockey_pump: {
    label: "Sprinkler Jockey Pump", short: "SJ", color: "#38bdf8",
    icon: Wrench,
    group: "pump_sprinkler",
  },
  diesel_pump: {
    label: "Diesel Pump", short: "DP", color: "#b45309",
    icon: Flame,
    group: "pump_backup",
  },
  standby_pump: {
    label: "Standby Pump", short: "ST", color: "#d97706",
    icon: Flame,
    group: "pump_backup",
  },
  booster_pump: {
    label: "Booster Pump", short: "BP", color: "#ec4899",
    icon: Radio,
    group: "pump_backup",
  },
  pump: {   // generic fallback
    label: "Pump", short: "PU", color: "#ff6b35",
    icon: Flame,
    group: "pump_hydrant",
  },
};

export const EQUIPMENT_GROUPS = [
  { key: "detection", label: "Detection", color: "#facc15" },
  { key: "pa", label: "PA / Alert", color: "#38bdf8" },
  { key: "sensor", label: "Sensors", color: "#22d3ee" },
  { key: "pump_hydrant", label: "Hydrant Pumps", color: "#ff6b35" },
  { key: "pump_sprinkler", label: "Sprinkler Pumps", color: "#4fd1ff" },
  { key: "pump_backup", label: "Backup / Booster", color: "#d97706" },
  { key: "infra", label: "Infrastructure", color: "#6366f1" },
];

// Derive live status ("ok" | "warn" | "fault" | "off") from telemetry + equipment type.
export function pinStatusFromTelemetry(deviceType, tele, deviceName = "") {
  if (!tele) return { code: "off", label: "OFFLINE" };
  const name = (deviceName || "").toLowerCase();
  switch (deviceType) {
    case "pressure_sensor": {
      const isSpk = name.includes("sprinkler");
      const v = isSpk ? tele.sprinkler_pressure_bar : tele.hydrant_pressure_bar;
      const min = isSpk ? 5 : 3.5;
      if (v == null) return { code: "off", label: "OFFLINE", value: "—" };
      if (v < min) return { code: "fault", label: "LOW PRESSURE", value: `${v.toFixed(2)} Bar` };
      if (v < min * 1.15) return { code: "warn", label: "WARNING", value: `${v.toFixed(2)} Bar` };
      return { code: "ok", label: "NORMAL", value: `${v.toFixed(2)} Bar` };
    }
    case "ultrasonic": {
      const isOHT = name.includes("oht") || name.includes("overhead");
      const isDiesel = name.includes("diesel");
      const v = isDiesel ? tele.diesel_tank_pct : isOHT ? tele.oht_tank_pct : tele.ug_tank_pct;
      if (v == null) return { code: "off", label: "OFFLINE" };
      if (v < 25) return { code: "fault", label: "CRITICAL LOW", value: `${v.toFixed(0)}%` };
      if (v < 50) return { code: "warn", label: "LOW", value: `${v.toFixed(0)}%` };
      return { code: "ok", label: "NORMAL", value: `${v.toFixed(0)}%` };
    }
    case "hydrant_main_pump": {
      const s = tele.main_hydrant_pump;
      if (s === "fault") return { code: "fault", label: "FAULT", value: s };
      return { code: s === "running" ? "ok" : "off", label: s === "running" ? "RUNNING" : "STOPPED", value: s };
    }
    case "hydrant_jockey_pump": {
      const s = tele.hydrant_jockey_pump || tele.jockey_pump;
      if (s === "fault") return { code: "fault", label: "FAULT" };
      return { code: s === "running" ? "ok" : "off", label: s === "running" ? "RUNNING" : "STANDBY", value: s };
    }
    case "sprinkler_main_pump": {
      const s = tele.main_sprinkler_pump;
      if (s === "fault") return { code: "fault", label: "FAULT" };
      return { code: s === "running" ? "ok" : "off", label: s === "running" ? "RUNNING" : "STOPPED", value: s };
    }
    case "sprinkler_jockey_pump": {
      const s = tele.sprinkler_jockey_pump || "stopped";
      return { code: s === "running" ? "ok" : "off", label: s === "running" ? "RUNNING" : "STANDBY", value: s };
    }
    case "standby_pump": {
      const s = tele.standby_pump;
      if (s === "fault") return { code: "fault", label: "FAULT" };
      return { code: s === "running" ? "ok" : "off", label: s === "running" ? "RUNNING" : "STANDBY", value: s };
    }
    case "diesel_pump": {
      const s = tele.diesel_pump || "stopped";
      if (s === "fault") return { code: "fault", label: "FAULT" };
      return { code: s === "running" ? "ok" : "off", label: s === "running" ? "RUNNING" : "STANDBY", value: s };
    }
    case "booster_pump": {
      const s = tele.booster_pump;
      return { code: s === "running" ? "ok" : "off", label: s === "running" ? "RUNNING" : "STANDBY", value: s };
    }
    case "pump": {  // fallback for any generic pump device
      const mapByName = {
        "main hydrant pump": tele.main_hydrant_pump,
        "standby diesel pump": tele.standby_pump,
        "jockey pump": tele.jockey_pump,
        "hydrant jockey pump": tele.hydrant_jockey_pump || tele.jockey_pump,
        "main sprinkler pump": tele.main_sprinkler_pump,
        "sprinkler jockey pump": tele.sprinkler_jockey_pump,
        "diesel pump": tele.diesel_pump,
        "booster pump": tele.booster_pump,
      };
      const s = mapByName[name] || "stopped";
      if (s === "fault") return { code: "fault", label: "FAULT" };
      return { code: s === "running" ? "ok" : "off", label: s === "running" ? "RUNNING" : "STOPPED", value: s };
    }
    case "smoke_detector":
    case "heat_detector": {
      if (tele.panel_alarms > 0) return { code: "fault", label: "ALARM" };
      return { code: "ok", label: "NORMAL" };
    }
    case "mcp":
      if (tele.panel_alarms > 0) return { code: "warn", label: "TRIGGERED" };
      return { code: "ok", label: "READY" };
    case "pa_speaker": {
      const s = tele.pa_system;
      if (s === "fault") return { code: "fault", label: "FAULT" };
      return { code: s === "working" ? "ok" : "off", label: s === "working" ? "ONLINE" : "OFF", value: s };
    }
    case "fire_panel":
      return { code: tele.panel_alarms > 0 ? "fault" : "ok", label: tele.panel_alarms > 0 ? "ALARM" : "WORKING" };
    case "gateway":
      return { code: "ok", label: "ONLINE" };
    default:
      return { code: "ok", label: "NORMAL" };
  }
}

export const STATUS_BG = {
  ok: "bg-emerald-500",
  warn: "bg-amber-500",
  fault: "bg-red-500",
  off: "bg-slate-500",
};
export const STATUS_RING = {
  ok: "ring-emerald-400/60 status-pulse-green",
  warn: "ring-amber-400/60",
  fault: "ring-red-400/70 status-pulse-red",
  off: "ring-slate-400/40",
};

export function equipmentMeta(deviceType) {
  return EQUIPMENT[deviceType] || EQUIPMENT.pump;
}
